import { useState } from 'react';
import { Button } from '../../../components/Button';
import { SelectPopover } from '../../../components/SelectPopover';
import { TextInput } from '../../../components/TextInput';
import { ApiError } from '../../../services/api';
import {
  createBillingContact,
  createStripeSubscription,
  ensureStripeCustomer,
  saveBillingPlan,
} from '../../../services/admin-billing';
import type {
  BillingInterval,
  BillingOrganizationSummary,
} from '../../../types/adminBilling';
import { INTERVAL_LABEL, dollarsToCents, formatMoney } from './billingFormat';
import { AdminPanel } from '../AdminPage';

const describe = (err: unknown, fallback: string): string =>
  err instanceof ApiError && err.message ? err.message : fallback;

const INTERVALS: readonly BillingInterval[] = ['Monthly', 'Annual'];
const INTERVAL_OPTIONS = INTERVALS.map((interval) => INTERVAL_LABEL[interval]);

const intervalFromLabel = (label: string): BillingInterval =>
  INTERVALS.find((interval) => INTERVAL_LABEL[interval] === label) ?? 'Monthly';

/** Today, as the date input wants it. Never through a Date in the other direction. */
const todayIso = (): string => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

interface BillingSetupWizardProps {
  organizationId: string;
  organizationName: string;
  summary: BillingOrganizationSummary | null;
  /** Called after each step commits, so the page's summary follows along. */
  onChanged: () => void;
}

/**
 * Sets an organization up to be billed, in one pass, in the order the pieces depend on each other.
 *
 * **Why a wizard and not the panels.** Everything here can already be done from the Plan, Contacts
 * and Stripe panels, and that was the problem: four panels in no stated order, where the plan has
 * to exist before Stripe will accept anything (the customer id is stored ON the plan row), the
 * contact has to exist before the customer has an email, and the recurring invoice has to be last
 * because it is the only step that actually bills anybody. An operator had to know all of that.
 *
 * **It writes at every step rather than at the end.** Each step calls the same endpoint its panel
 * calls, and the work survives a closed tab. A wizard that collected four screens of input and then
 * made four calls would, on a failure at the third, leave an organization half set up with no way
 * to tell which half - which is the state this is trying to prevent.
 *
 * **It disappears once setup is done.** The panels are the right shape for editing one thing later;
 * this is only for the first time through.
 */
export const BillingSetupWizard = ({
  organizationId,
  organizationName,
  summary,
  onChanged,
}: BillingSetupWizardProps) => {
  const plan = summary?.plan ?? null;
  const hasPlan = plan !== null;
  const hasContact = (summary?.activeToContactCount ?? 0) > 0;
  const hasCustomer = summary?.isStripeManaged ?? false;

  const [amount, setAmount] = useState('');
  const [interval, setInterval] = useState<BillingInterval>('Monthly');
  const [terms, setTerms] = useState('30');
  const [firstInvoiceOn, setFirstInvoiceOn] = useState(todayIso());
  const [email, setEmail] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (work: () => Promise<void>, fallback: string) => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await work();
      onChanged();
    } catch (err: unknown) {
      setError(describe(err, fallback));
    } finally {
      setSaving(false);
    }
  };

  const savePlanStep = () => {
    const amountCents = dollarsToCents(amount);
    if (amountCents === null || amountCents <= 0) {
      setError('Enter what they owe each period, in dollars.');
      return;
    }
    const paymentTermsDays = Number(terms.trim());
    if (!Number.isInteger(paymentTermsDays) || paymentTermsDays < 0) {
      setError('Payment terms must be a whole number of days.');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(firstInvoiceOn)) {
      setError('Pick the date of the first invoice.');
      return;
    }

    return run(
      async () => {
        await saveBillingPlan(organizationId, {
          amountCents,
          currency: 'USD',
          interval,
          paymentTermsDays,
          firstInvoiceOn,
          status: 'Active',
          notes: '',
        });
      },
      'Could not save the plan.'
    );
  };

  const saveContactStep = () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Enter the accounts payable address to send the invoice to.');
      return;
    }

    return run(
      async () => {
        // Always a To. Stripe emails the customer's one address, and the first active To is what
        // becomes it.
        await createBillingContact(organizationId, {
          email: trimmed,
          name: null,
          kind: 'To',
          isActive: true,
        });
      },
      'Could not save the billing contact.'
    );
  };

  const createCustomerStep = () =>
    run(
      async () => {
        // No details passed: the API falls back to the organization's own name and its first
        // active To contact, which is exactly what the two steps above just recorded.
        await ensureStripeCustomer(organizationId);
      },
      'Could not create the Stripe customer.'
    );

  const startInvoicingStep = () =>
    run(
      async () => {
        // No body: the amount, interval and terms all default to the plan, so the recurring
        // invoice cannot disagree with what was agreed a moment ago.
        await createStripeSubscription(organizationId);
      },
      'Could not set up the recurring invoice.'
    );

  const step = !hasPlan ? 1 : !hasContact ? 2 : !hasCustomer ? 3 : 4;

  return (
    // The wrapper carries the test id, which AdminPanel does not pass through.
    <AdminPanel data-testid="billing-setup-wizard"
      title={<>Set up billing for {organizationName}</>}
      titleId="billing-wizard-title"
      description={`Step ${step} of 4. Each step is saved as you go, so you can leave and come back.`}
    >
      {error && (
        <p className="billing-form__error" role="alert" data-testid="billing-wizard-error">
          {error}
        </p>
      )}

      {step === 1 && (
        <div className="billing-form" data-testid="billing-wizard-step-plan">
          <p className="billing-panel__note">What does this organization owe, and how often?</p>
          <TextInput
            label="Amount each period (dollars)"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={saving}
            data-testid="billing-wizard-amount"
          />
          <div>
            <span className="billing-panel__label" id="billing-wizard-interval-label">
              How often
            </span>
            <SelectPopover
              options={INTERVAL_OPTIONS}
              value={INTERVAL_LABEL[interval]}
              onChange={(label) => setInterval(intervalFromLabel(label))}
              placeholder="How often"
              clearable={false}
              disabled={saving}
              fullWidth
              testId="billing-wizard-interval"
            />
          </div>
          <TextInput
            label="First invoice date"
            type="date"
            value={firstInvoiceOn}
            onChange={(e) => setFirstInvoiceOn(e.target.value)}
            disabled={saving}
            data-testid="billing-wizard-first-invoice"
          />
          <TextInput
            label="Payment terms (days to pay)"
            inputMode="numeric"
            value={terms}
            onChange={(e) => setTerms(e.target.value)}
            disabled={saving}
            data-testid="billing-wizard-terms"
          />
          <Button
            variant="primary"
            onClick={savePlanStep}
            disabled={saving}
            data-testid="billing-wizard-save-plan"
          >
            {saving ? 'Saving...' : 'Save and continue'}
          </Button>
        </div>
      )}

      {step === 2 && (
        <div className="billing-form" data-testid="billing-wizard-step-contact">
          <p className="billing-panel__note">
            Where should the invoice go? This becomes the Stripe customer&apos;s email, so it is the
            address Stripe sends to. An accounts payable distribution list works well here, since
            Stripe emails one address.
          </p>
          <TextInput
            label="Accounts payable email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={saving}
            data-testid="billing-wizard-email"
          />
          <Button
            variant="primary"
            onClick={saveContactStep}
            disabled={saving}
            data-testid="billing-wizard-save-contact"
          >
            {saving ? 'Saving...' : 'Save and continue'}
          </Button>
        </div>
      )}

      {step === 3 && (
        <div className="billing-form" data-testid="billing-wizard-step-customer">
          <p className="billing-panel__note">
            Create this organization at Stripe. Its name and invoice email come from what you just
            entered. Nothing is billed yet.
          </p>
          <Button
            variant="primary"
            onClick={createCustomerStep}
            disabled={saving}
            data-testid="billing-wizard-create-customer"
          >
            {saving ? 'Creating...' : 'Create the Stripe customer'}
          </Button>
          <p className="billing-form__hint">
            Already have one at Stripe? Link it instead from the Stripe panel below.
          </p>
        </div>
      )}

      {step === 4 && (
        <div className="billing-form" data-testid="billing-wizard-step-invoice">
          <p className="billing-panel__note">
            Last step. Stripe will raise and email an invoice for{' '}
            {plan ? formatMoney(plan.amountCents, plan.currency) : 'the plan amount'}{' '}
            {plan ? INTERVAL_LABEL[plan.interval].toLowerCase() : ''}, starting{' '}
            {plan?.firstInvoiceOn ?? 'on the first invoice date'}. Nobody is billed until this
            exists.
          </p>
          <Button
            variant="primary"
            onClick={startInvoicingStep}
            disabled={saving}
            data-testid="billing-wizard-start-invoicing"
          >
            {saving ? 'Setting up...' : 'Start invoicing'}
          </Button>
          <p className="billing-form__hint">
            Sent by Stripe with your payment terms on it. The customer pays by card or bank transfer
            from the invoice, and nothing is charged automatically.
          </p>
        </div>
      )}
    </AdminPanel>
  );
};
